import React, { useState } from 'react'
import { Check, Pencil, Trophy } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import {
  BONUS_CPM,
  GIFTING_ACTIVATION_FEE,
  GIFTING_USAGE_DAYS,
  suggestedBonuses,
  unlockedBonuses,
  viewsLabel,
  type BonusTier,
} from '@adthlete/common/utils/dealPricing'

const money = (n: number) => `$${n.toLocaleString('en-US')}`
const same = (a: BonusTier[], b: BonusTier[]) =>
  a.length === b.length && a.every((t, i) => t.views === b[i].views && t.amount === b[i].amount)

/**
 * The gifting bonus ladder: what an athlete earns as their gifting post passes each view
 * milestone (cumulative). Starts at Athios's market-based suggestion; the brand keeps it
 * ("Looks good") or sets its own amounts. Also states what every gifting deal includes.
 */
export const GiftingBonusEditor: React.FC<{
  value: BonusTier[] | null | undefined
  onChange: (tiers: BonusTier[]) => void
  compact?: boolean
}> = ({ value, onChange, compact }) => {
  const suggested = suggestedBonuses()
  const tiers = value?.length ? value : suggested
  const isSuggested = same(tiers, suggested)
  const [editing, setEditing] = useState(!isSuggested)
  const example = unlockedBonuses(30_000, tiers)
  const max = tiers.reduce((s, t) => s + t.amount, 0)

  return (
    <div className="space-y-4">
      {!compact ? (
        <ul className="grid gap-2 text-sm text-gray-700 sm:grid-cols-3">
          <li className="rounded-lg border border-foreground/10 px-3 py-2">
            <span className="block font-semibold text-foreground">
              {money(GIFTING_ACTIVATION_FEE)} per athlete
            </span>
            Athios activation fee
          </li>
          <li className="rounded-lg border border-foreground/10 px-3 py-2">
            <span className="block font-semibold text-foreground">
              {GIFTING_USAGE_DAYS} days of paid usage
            </span>
            Run every post as an ad
          </li>
          <li className="rounded-lg border border-foreground/10 px-3 py-2">
            <span className="block font-semibold text-foreground">Bonuses on views</span>
            Paid only when posts perform
          </li>
        </ul>
      ) : null}

      <div className="rounded-xl border border-foreground/10">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-foreground/10 px-4 py-3">
          <p className="flex items-center gap-2 font-semibold text-foreground">
            <Trophy className="h-4 w-4 text-[#B45309]" />
            Performance bonuses
          </p>
          <div className="flex gap-1">
            <button
              type="button"
              aria-pressed={!editing}
              onClick={() => {
                setEditing(false)
                onChange(suggested)
              }}
              className={cn(
                'inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-semibold',
                !editing ? 'bg-[#C0FD72] text-black' : 'text-gray-500 hover:text-foreground',
              )}
            >
              <Check className="h-4 w-4" /> Looks good
            </button>
            <button
              type="button"
              aria-pressed={editing}
              onClick={() => setEditing(true)}
              className={cn(
                'inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-semibold',
                editing ? 'bg-[#C0FD72] text-black' : 'text-gray-500 hover:text-foreground',
              )}
            >
              <Pencil className="h-4 w-4" /> Custom
            </button>
          </div>
        </div>
        <ul className="divide-y divide-foreground/10">
          {tiers.map((t, i) => (
            <li key={t.views} className="flex items-center justify-between gap-3 px-4 py-2.5">
              <span className="text-sm text-gray-700">
                Post reaches{' '}
                <span className="font-semibold text-foreground">{viewsLabel(t.views)} views</span>
              </span>
              {editing ? (
                <span className="flex items-center gap-1">
                  <span className="text-sm text-gray-500">$</span>
                  <Input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    value={t.amount}
                    aria-label={`Bonus at ${viewsLabel(t.views)} views`}
                    onChange={(e) =>
                      onChange(
                        tiers.map((x, j) =>
                          j === i ? { ...x, amount: Math.max(0, Number(e.target.value) || 0) } : x,
                        ),
                      )
                    }
                    className="h-9 w-24 border-foreground/15 bg-card text-right text-base tabular-nums"
                  />
                </span>
              ) : (
                <span className="font-semibold tabular-nums text-foreground">
                  +{money(t.amount)}
                </span>
              )}
            </li>
          ))}
        </ul>
        <div className="space-y-1 border-t border-foreground/10 px-4 py-3 text-xs text-gray-500">
          <p>
            Bonuses stack as a post passes each milestone. A post at 30K views earns{' '}
            <span className="font-semibold text-foreground">{money(example.total)}</span>; the most
            one post can earn is{' '}
            <span className="font-semibold text-foreground">{money(max)}</span> at 1M views.
          </p>
          {isSuggested ? (
            <p>
              Suggested from a ${BONUS_CPM} paid-social CPM: at 1M views you pay about $
              {((max / 1_000_000) * 1000).toFixed(2)} per 1,000 views, well under the cost of the
              same reach in ads.
            </p>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default GiftingBonusEditor
